import { QuestionRenderer } from 'modules/questions/QuestionRenderer';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { loadMathJaxScript } from './loadMathJaxScript';
import { typesetMath, convertMathJax } from 'modules/assessment/AssessmentApp';

const Icons = {
    Brain: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
    Close: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
    Check: () => <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"></path></svg>,
    Strategy: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    Proof: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    Globe: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>,
    LightBulb: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
};

const MathJaxBlock = React.memo(({ html, className, isBlock = true }) => {
    const nodeRef = useRef(null);
    useEffect(() => {
        if (nodeRef.current && window.MathJax && window.MathJax.Hub) {
            window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, nodeRef.current]);
        }
    }, [html]);
    const Tag = isBlock ? 'div' : 'span';
    return <Tag ref={nodeRef} className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />;
});

const FinalAnswerWorkspace = ({ answerChoices, stepsCount, finalSolution }) => {
    // Determine the exact expected answer, either from finalSolution string or from answerChoices
    let rawAnswer = finalSolution || answerChoices?.find(c => c.isCorrect)?.text || answerChoices?.[0]?.text || "";
    // If it's an object/number, stringify it safely
    let expectedAnswerText = typeof rawAnswer === 'object' ? JSON.stringify(rawAnswer) : String(rawAnswer);
    
    // If the answer is raw math without delimiters (common for AI final solutions), wrap it
    if (expectedAnswerText && !expectedAnswerText.includes('\\(') && !expectedAnswerText.includes('\\[')) {
        if (expectedAnswerText.includes('\\') || /^[0-9\.\+\-\*\/\^\=]+$/.test(expectedAnswerText.trim())) {
            expectedAnswerText = `\\( ${expectedAnswerText} \\)`;
        }
    }
    const explanation = answerChoices?.find(c => c.isCorrect)?.explanation || answerChoices?.[0]?.explanation;

    return (
        <div className="space-y-8 animate-in slide-in-from-right-4 duration-500 flex-1">
            <div className="flex items-center gap-4">
                <span className="text-5xl font-black text-slate-100 dark:text-slate-800 select-none leading-none">0{stepsCount + 1}</span>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white uppercase tracking-tight leading-none">Final Solution</h2>
            </div>

            <div className="p-8 rounded-2xl border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-bl-lg">Resolution</div>
                
                <div className="flex flex-col gap-6 mt-4">
                    <div>
                        <h4 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3">Final Expected Answer</h4>
                        <MathJaxBlock className="text-base text-slate-900 dark:text-white bg-white dark:bg-slate-950 p-6 rounded-xl shadow-sm border border-emerald-100 dark:border-emerald-800/50 overflow-x-auto" html={convertMathJax(expectedAnswerText)} />
                    </div>

                    {explanation && (
                        <div>
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Explanation</h4>
                            <MathJaxBlock className="text-base text-slate-700 dark:text-slate-300 leading-relaxed p-5 bg-white/60 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-800" html={convertMathJax(String(explanation))} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProblemSolverModal = ({ isOpen, onClose, data }) => {
    const modalRef = useRef(null);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [leftToggle, setLeftToggle] = useState('strategy');

    const [showAnswerChoices, setShowAnswerChoices] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const parsedWorkflow = useMemo(() => {
        if (!data?.workflowData) return null;
        try {
            const raw = data.workflowData;
            const payload = raw.result ? (typeof raw.result === 'string' ? JSON.parse(raw.result) : raw.result) : raw;
            return typeof payload === 'string' ? JSON.parse(payload) : payload;
        } catch (e) { return null; }
    }, [data]);

    const steps = parsedWorkflow?.steps || [];
    const lifelines = parsedWorkflow?.lifelines || {};
    const questionRequest = data?.questionRequest || {};
    const answerChoices = parsedWorkflow?.answerChoices || [];

    // Modal behavior and scroll lock
    useEffect(() => {
        loadMathJaxScript();
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setCompletedSteps([]);
            setActiveIdx(0);
            setLeftToggle('strategy');
            setShowAnswerChoices(false);
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // Modal content MathJax typesetting is handled by individual components
    // just like QuestionRenderer.jsx does.

    if (!isOpen || !parsedWorkflow) return null;

    const formatSteps = (strategy) => {
        if (!strategy) return "";
        if (typeof strategy === 'string') return strategy;
        if (Array.isArray(strategy)) {
            return strategy.map(step => `<div class="mb-3"><p class="font-medium">${step}</p></div>`).join('');
        }
        return JSON.stringify(strategy);
    };

    const handleStepSubmit = (stepIdx, userInputs) => {
        const step = steps[stepIdx];
        if (!step) return;
        let isCorrect = true;
        (step.inputs || []).forEach((inp, i) => {
            const u = (userInputs[i] || "").toString().trim().toLowerCase().replace(/\s/g, '');
            const c = String(inp.correctValue || "").trim().toLowerCase().replace(/\s/g, '');
            if (u !== c) isCorrect = false;
        });
        setCompletedSteps(prev => [...prev, { stepIdx, isCorrect, userInputs }]);
    };

    return (
        <div ref={modalRef} className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4 backdrop-blur-md transition-all duration-300">
            <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />

            <div className="relative bg-white dark:bg-slate-900 w-full max-w-6xl h-full sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl flex flex-col overflow-y-auto sm:overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-800">

                {/* Sticky Header */}
                <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-200">
                            <Icons.Brain />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tighter leading-none">Guided Resolution</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">LaTeX & Scientific Rendering Active</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-all text-slate-400">
                        <Icons.Close />
                    </button>
                </header>

                <div className="flex-1 overflow-visible sm:overflow-hidden flex flex-col lg:flex-row">

                    {/* Left Column: Context Base */}
                    <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-slate-800 border-b lg:border-b-0 lg:border-r border-slate-100 dark:border-slate-700 shrink-0 lg:overflow-y-auto">
                        <div className="p-6 sm:p-8 space-y-6">
                            <section>
                                <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-3">Problem Foundation</h4>
                                <div className="text-sm bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <QuestionRenderer question={questionRequest} index={0} isPaperView={true} />
                                </div>
                            </section>

                            <div className="bg-slate-100 dark:bg-slate-950/50 p-1 rounded-xl flex gap-1">
                                <ToggleButton active={leftToggle === 'strategy'} onClick={() => setLeftToggle('strategy')} label="Strategy" icon={<Icons.Strategy />} />
                                <ToggleButton active={leftToggle === 'proof'} onClick={() => setLeftToggle('proof')} label="Theory" icon={<Icons.Proof />} />
                                <ToggleButton active={leftToggle === 'example'} onClick={() => setLeftToggle('example')} label="Context" icon={<Icons.Globe />} />
                            </div>

                            <div className="animate-in fade-in duration-300 min-h-[160px]">
                                {leftToggle === 'strategy' && <SupportBox color="indigo" title="Logic Path" content={lifelines?.aiStrategy ? formatSteps(lifelines.aiStrategy) : ""} />}
                                {leftToggle === 'proof' && <SupportBox color="blue" title="Scientific Proof" content={lifelines?.textbook ? `<div class="space-y-4"><div><h5 class="font-bold text-sm text-blue-700 dark:text-blue-300 mb-2">${lifelines.textbook.concept}</h5><p class="text-sm leading-relaxed">${lifelines.textbook.theory}</p></div><div class="border-t border-blue-200 dark:border-blue-800 pt-3"><h6 class="font-semibold text-xs text-blue-600 dark:text-blue-400 uppercase mb-2">Formulas</h6><div class="space-y-2">${Array.isArray(lifelines.textbook.formulas) ? lifelines.textbook.formulas.map(f => `<div class="bg-blue-100 dark:bg-blue-900/30 p-2 rounded text-xs font-mono">${f}</div>`).join('') : `<div class="bg-blue-100 dark:bg-blue-900/30 p-2 rounded text-xs font-mono">${lifelines.textbook.formulas || ""}</div>`}</div></div></div>` : ""} />}
                                {leftToggle === 'example' && <SupportBox color="emerald" title="Real World" content={lifelines?.realWorld} />}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Interaction Workspace */}
                    <div className="w-full lg:w-1/2 flex flex-col bg-slate-50/50 dark:bg-slate-950/20 shrink-0 lg:overflow-y-auto border-t lg:border-t-0 border-slate-100 dark:border-slate-800">

                        {/* Step Tab Bar (Sticky on Right Side) */}
                        <div className="sticky top-[72px] lg:top-0 z-20 bg-white dark:bg-slate-800 px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 flex gap-2 overflow-x-auto no-scrollbar shrink-0 shadow-sm">
                            {steps.map((_, i) => {
                                const isCompleted = completedSteps.some(s => s.stepIdx === i);
                                return (
                                    <button
                                        key={i}
                                        onClick={() => { setActiveIdx(i); setShowAnswerChoices(false); }}
                                        className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all whitespace-nowrap border-2
                                            ${activeIdx === i && !showAnswerChoices ? 'bg-slate-900 text-white border-slate-900 dark:bg-indigo-600 dark:border-indigo-600 shadow-md' : 'bg-white dark:bg-slate-800 text-slate-400 border-slate-100 dark:border-slate-700'}
                                            ${isCompleted && (activeIdx !== i || showAnswerChoices) ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : ''}
                                        `}
                                    >
                                        Step {i + 1} {isCompleted && <Icons.Check />}
                                    </button>
                                );
                            })}
                            {steps.length > 0 && (
                                <button
                                    onClick={() => setShowAnswerChoices(true)}
                                    disabled={completedSteps.length !== steps.length}
                                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase transition-all whitespace-nowrap border-2
                                        ${showAnswerChoices ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white dark:bg-slate-800 text-slate-400 border-slate-100 dark:border-slate-700'}
                                        ${completedSteps.length !== steps.length ? 'cursor-not-allowed opacity-50' : ''}
                                    `}
                                >
                                    <Icons.LightBulb /> Answer
                                </button>
                            )}
                        </div>

                        <div className="p-6 sm:p-10 flex-1">
                            {answerChoices && showAnswerChoices ? (
                                <FinalAnswerWorkspace
                                    answerChoices={answerChoices}
                                    finalSolution={parsedWorkflow?.finalSolution || parsedWorkflow?.solution}
                                    stepsCount={steps.length}
                                />
                            ) : steps[activeIdx] ? (
                                <ActiveStepWorkspace
                                    step={steps[activeIdx]}
                                    index={activeIdx}
                                    result={completedSteps.find(s => s.stepIdx === activeIdx)}
                                    onSubmit={(inputs) => handleStepSubmit(activeIdx, inputs)}
                                />
                            ) : (
                                <div className="text-center py-20 text-slate-300 font-bold uppercase tracking-widest">Select step to begin</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer (Sticky) */}
                <footer className="sticky bottom-0 z-30 px-6 sm:px-8 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
                    <button onClick={onClose} className="px-6 py-2 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 dark:hover:text-white transition-all">Dismiss</button>
                    {completedSteps.length === steps.length && steps.length > 0 && (
                        <button onClick={onClose} className="px-8 py-2 bg-slate-900 dark:bg-indigo-600 text-white text-[11px] font-black uppercase tracking-widest rounded-lg shadow-xl">Complete Resolution</button>
                    )}
                </footer>
            </div>
        </div>
    );
};

/* --- Refined Sub-components --- */

const ToggleButton = ({ active, onClick, label, icon }) => (
    <button onClick={onClick} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-tighter transition-all ${active ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
        {icon} <span className="hidden xs:inline">{label}</span>
    </button>
);

const SupportBox = ({ title, content, color }) => {
    const accents = {
        indigo: "text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-900 bg-indigo-50/30",
        blue: "text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900 bg-blue-50/30",
        emerald: "text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900 bg-emerald-50/30"
    };
    return (
        <div className={`p-5 rounded-xl border-l-4 ${accents[color]}`}>
            <p className="text-[9px] font-black uppercase mb-2 opacity-60 tracking-[0.2em]">{title}</p>
            <MathJaxBlock className="text-[13px] leading-relaxed font-medium" html={convertMathJax(content || "")} />
        </div>
    );
};

const ActiveStepWorkspace = ({ step, index, result, onSubmit }) => {
    const [inputs, setInputs] = useState({});

    // Clear local inputs when switching steps
    useEffect(() => { setInputs({}); }, [index]);

    return (
        <div className="space-y-8 animate-in slide-in-from-right-4 duration-500 flex-1">
            <div className="flex items-center gap-4">
                <span className="text-5xl font-black text-slate-100 dark:text-slate-800 select-none leading-none">0{index + 1}</span>
                <MathJaxBlock className="text-xl font-bold text-slate-900 dark:text-white uppercase tracking-tight leading-none" html={convertMathJax(step?.title || "Resolution Step")} />
            </div>

            <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm leading-relaxed text-slate-600 dark:text-slate-300 text-base space-y-4">
                <div className="flex items-start gap-3">
                    <MathJaxBlock className="italic font-medium opacity-90" html={convertMathJax(step?.instruction || "")} />
                </div>
                <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0zM8 9a1 1 0 100-2 1 1 0 000 2zm5-1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" /></svg>
                    <MathJaxBlock className="italic text-sm opacity-75 border-l-2 border-indigo-300 dark:border-indigo-700 pl-3" html={convertMathJax(step?.reference || "")} />
                </div>
            </div>


            {result ? (
                <div className={`p-8 rounded-xl border-l-[10px] animate-in zoom-in-95 ${result.isCorrect ? 'bg-emerald-50 border-emerald-500 text-emerald-900' : 'bg-amber-50 border-amber-500 text-amber-900'}`}>
                    <h5 className="font-black text-[10px] uppercase tracking-widest mb-3">{result.isCorrect ? 'Verification Status: Correct' : 'Adjustment Hint'}</h5>
                    <MathJaxBlock className="text-sm font-medium leading-relaxed" html={convertMathJax((result.isCorrect ? step?.successMessage : step?.revealMessage) || "")} />
                </div>
            ) : (
                <div className="space-y-5">
                    {(step?.inputs || []).map((inp, i) => (
                        <div key={i}>
                            <MathJaxBlock className="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1 tracking-widest" html={convertMathJax(inp?.label || "Answer")} />
                            <div className="flex gap-2 items-center">
                                <input
                                    autoFocus={i === 0} type="text"
                                    className="flex-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-5 py-4 text-base outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 dark:text-white transition-all shadow-inner"
                                    placeholder="Input analytical value..."
                                    value={inputs[i] || ""}
                                    onChange={(e) => setInputs(prev => ({ ...prev, [i]: e.target.value }))}
                                />
                                {i === (step?.inputs || []).length - 1 && (
                                    <button
                                        onClick={() => onSubmit(inputs)}
                                        className="p-3 bg-slate-900 dark:bg-indigo-600 text-white rounded-xl hover:shadow-lg transition-all active:scale-95 flex-shrink-0"
                                        title="Check Logic"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" /></svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProblemSolverModal;